import { injectable, inject } from 'inversify'
import Notifications from '@kyvg/vue3-notification'
import { Quasar } from 'quasar'
import { SERVICE_TYPES } from '@/core/container/types'
// eslint-disable-next-line
import type { App, Component } from '@/composition/vue/compositionApi'
// eslint-disable-next-line
import { createApp } from '@/composition/vue/compositionApi'
import type VuexService from '@/services/vuex/vuexService'
import type RouterService from '@/services/route/routerService'
import type { RouterBuilderInterface } from '@/services/route/types'
import type TranslateService from '@/services/translateService'
import quasarOptions from '@/quasarOptions'
import type TrackingService from '@/services/tracking/trackingService'
import type AuthService from '@/services/auth/authService'
import type { MonitoringServiceInterface } from '@/services/monitoring/types'
import { isDev, isAppModuleDebug, isAppSharedComposableDebug } from '@/utils/system'
import { addSandboxRoutes } from '@/routes'
import { setupModuleDevtools, setupSharedComposableDevtools } from '@textmagic/vite-plugin-devtools-appmodule'
import type WindowService from '@/services/browser/windowService'
import { safeHtmlDirective } from '@/directives/safe-html/safe-html.directive'
import { outerClickDirective } from '@/directives/outer-click.directive'
import { tooltipDirective } from '@/directives/tooltip.directive'
import { testIdDirective } from '@/directives/test-id.directive'
// import LagMonitor from '@/plugins/lagMonitor'

import TmRouterViewReloader from '@/components/shared/TmRouterViewReloader.vue'
import DetailsHero from '@/components/shared/templates/details/DetailsHero.vue'
import TmDropdownSelect from '@/components/shared/dropdowns/TmDropdownSelect.vue'
import TmFormLine from '@/components/shared/TmFormLine.vue'
import TmOverlay from '@/components/layout/TmOverlay.vue'
import TmIcon from '@/components/shared/TmIcon.vue'
import TmButton from '@/components/shared/TmButton.vue'
import TmFlag from '@/components/shared/TmFlag.vue'
import TmAvatar from '@/components/shared/TmAvatar.vue'
import TmPerson from '@/components/shared/TmPerson.vue'
import TmCountry from '@/components/shared/TmCountry.vue'
import TmSearch from '@/components/shared/TmSearch.vue'
import TmOnlineStatusBadge from '@/components/shared/TmOnlineStatusBadge.vue'
import TmAlert from '@/components/shared/TmAlert.vue'
import TmTooltip from '@/components/shared/tooltip/TmTooltip.vue'
import TmPersistentTooltip from '@/components/shared/tooltip/TmPersistentTooltip.vue'
import Resolver from '@/components/resolvers/Resolver.vue'
import PageResolver from '@/components/resolvers/PageResolver.vue'
import AsyncResolver from '@/components/resolvers/AsyncResolver.vue'
import RedirectToIfNotFoundResolver from '@/components/resolvers/RedirectToIfNotFoundResolver.vue'
import NotFoundPageResolver from '@/components/resolvers/NotFoundPageResolver.vue'
import DetailsPageResolver from '@/components/resolvers/DetailsPageResolver.vue'
import SuspenseResolver from '@/components/resolvers/SuspenseResolver.vue'
import TmCard from '@/components/shared/cards/TmCard.vue'
import TmTabs from '@/components/shared/TmTabs.vue'
import TmProgress from '@/components/shared/TmProgress.vue'
import TmWatchTutorial from '@/components/shared/TmWatchTutorial.vue'
import TmBadge from '@/components/shared/badge/TmBadge.vue'
import TmFaq from '@/components/shared/faq/TmFaq.vue'
import TmFaqCard from '@/components/shared/faq/TmFaqCard.vue'
import TmFaqCardSection from '@/components/shared/faq/TmFaqCardSection.vue'
import TmFaqExpansionItem from '@/components/shared/faq/TmFaqExpansionItem.vue'
import TmFaqHeader from '@/components/shared/faq/TmFaqHeader.vue'
import TmFaqParagraph from '@/components/shared/faq/TmFaqParagraph.vue'
import TmFaqTabs from '@/components/shared/faq/TmFaqTabs.vue'
import TmInteractiveList from '@/components/shared/TmInteractiveList.vue'
import TmColoredUl from '@/components/shared/templates/TmColoredUl.vue'
import TmEllipsis from '@/components/shared/TmEllipsis.vue'
import TmPaging from '@/components/shared/paging/TmPaging.vue'
import TmPersonBase from '@/components/shared/TmPersonBase.vue'
import TmVirtualScroll from '@/components/shared/tmVirtualScroll/TmVirtualScroll.vue'
import TmCaptcha from '@/components/shared/TmCaptcha.vue'
import TmTranslate from '@/components/TmTranslate.vue'
import TmFormattedTextRenderer from '@/components/shared/TmFormattedTextRenderer.vue'
import TmSkeleton from '@/components/shared/skeletons/TmSkeleton.vue'
import TmRouterView from '@/components/shared/TmRouterView.vue'
import PageContent from '@/components/layout/pageContent/PageContent.vue'
import SinglePageContent from '@/components/layout/pageContent/SinglePageContent.vue'
import MainSidebar from '@/components/layout/mainSidebar/MainSidebar.vue'

export const GLOBAL_COMPONENTS = {
  Resolver,
  PageResolver,
  AsyncResolver,
  RedirectToIfNotFoundResolver,
  NotFoundPageResolver,
  DetailsPageResolver,
  SuspenseResolver,
  TmRouterViewReloader,
  DetailsHero,
  TmDropdownSelect,
  TmFormLine,
  TmOverlay,
  TmIcon,
  TmFlag,
  TmAvatar,
  TmPerson,
  TmCountry,
  TmSearch,
  TmOnlineStatusBadge,
  TmAlert,
  TmTooltip,
  TmPersistentTooltip,
  TmButton,
  TmCard,
  TmTabs,
  TmProgress,
  TmWatchTutorial,
  TmBadge,
  TmFaq,
  TmFaqCard,
  TmFaqCardSection,
  TmFaqExpansionItem,
  TmFaqHeader,
  TmFaqParagraph,
  TmFaqTabs,
  TmInteractiveList,
  TmColoredUl,
  TmEllipsis,
  TmPaging,
  TmPersonBase,
  TmVirtualScroll,
  TmCaptcha,
  TmTranslate,
  TmFormattedTextRenderer,
  TmSkeleton,
  PageContent,
  SinglePageContent,
  MainSidebar,
  RouterView: TmRouterView,
} satisfies Record<string, Component>

@injectable()
class VueService {
  constructor(
    @inject(SERVICE_TYPES.VuexService) protected readonly vuexService: VuexService,
    @inject(SERVICE_TYPES.RouterService) protected readonly routerService: RouterService,
    @inject(SERVICE_TYPES.RouterBuilderService) protected readonly routerBuilderService: RouterBuilderInterface,
    @inject(SERVICE_TYPES.TranslateService) protected readonly translateService: TranslateService,
    @inject(SERVICE_TYPES.MonitoringService)
    protected readonly monitoringService: MonitoringServiceInterface,
    @inject(SERVICE_TYPES.TrackingService) protected readonly trackingService: TrackingService,
    @inject(SERVICE_TYPES.AuthService) protected readonly authService: AuthService,
    @inject(SERVICE_TYPES.WindowService) protected readonly windowService: WindowService,
  ) {}

  public createVueApp(rootComponent: Component, props?: Record<string, unknown> | null) {
    return createApp(rootComponent, props)
  }

  public async create(selector: string, rootComponent: Component): Promise<App> {
    const app = this.createVueApp(rootComponent)
    app.config.performance = isDev
    const router = this.routerService.getRouter()
    this.monitoringService.init(app, router)
    this.trackingService.registerApp(app)
    app.use(Quasar, quasarOptions)
    app.use(this.vuexService.getStore())

    app.config.globalProperties.$t = this.translateService.t as typeof app.config.globalProperties.$t
    app.config.globalProperties.$tc = this.translateService.tc as typeof app.config.globalProperties.$tc
    app.use(this.translateService.getPlugin())
    // app.use(LagMonitor, {
    //   interval: 200,
    //   longTaskThreshold: 2000,
    //   logDest: isDev() ? 'both' : 'sentry',
    // })

    await addSandboxRoutes(this.routerBuilderService)

    const getCurrentUser: (() => Promise<Response>) | null = (this.windowService.self() as any).__getCurrentUser

    if (getCurrentUser) {
      await this.authService.tryFillCurrentUser((_userId: string | null) => {
        app.use(router)
      }, true)
    } else {
      app.use(router)
    }
    app.use(Notifications)

    Object.entries(GLOBAL_COMPONENTS).forEach(([key, component]) => {
      if (key === 'RouterView') {
        delete app._context.components[key]
      }

      app.component(key, component)
    })

    app.directive('safe-html', safeHtmlDirective)
    app.directive('outer-click', outerClickDirective)
    app.directive('tooltip', tooltipDirective)
    app.directive('test-id', testIdDirective)

    // #region Related to vite-plugin-devtools-appmodule
    // The import.meta.env.DEV wrapper keeps @vue/devtools-kit (~53 KB) out of the prod bundle;
    // Rolldown resolves the imported flags too late to drop the module behind them.
    if (import.meta.env.DEV) {
      if (isAppModuleDebug) {
        setupModuleDevtools(app)
      }

      if (isAppSharedComposableDebug) {
        setupSharedComposableDevtools(app)
      }
    }
    // #endregion

    app.mount(selector)

    return app
  }
}

export default VueService
